/**
 * EL ADAPTADOR DE AUDITORÍA Y ANALÍTICA.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ESTE DOMINIO ES EL ENSAYO DE LA MIGRACIÓN, NO UNA FUNCIONALIDAD.         ║
 * ║  Va primero porque es el único sitio donde equivocarse no le cuesta nada  ║
 * ║  a un cliente: solo admin, sin dinero, sin tiempo real, sin disparadores  ║
 * ║  de Firestore (PLAN-MIGRACION §5). Lo que se prueba aquí es la TUBERÍA    ║
 * ║  —esquema, lectura dual, interruptor y REVERSIÓN—, y por eso el runbook   ║
 * ║  (`docs-internos/RUNBOOK-REVERSION-AUDIT.md`) pesa más que el cableado.   ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * ─── 🔴 EL DOMINIO ES DE SOLO LECTURA, Y ESO NO ES UN DESCUIDO ──────────────
 *
 * `api/src/routes/audit.ts` NO tiene ni una ruta de escritura, a propósito: un
 * endpoint para escribir auditoría es un endpoint para escribir auditoría falsa.
 * Consecuencia directa para la app, y hay que tenerla delante al leer el
 * runbook: **poner `audit` en `postgres` NO cambia dónde se escribe**. Los
 * registros de admin (`src/utils/adminAudit.js`), los contadores de pantallas y
 * búsquedas (`src/utils/analytics.js`), los eventos de perfil y el alta/apagado
 * de avisos siguen yendo a Firestore, siempre.
 *
 * O sea: con `read:'postgres'` y `shadow:false` el panel lee una base donde
 * nadie está escribiendo. Es el "estado ILEGAL" que `migrationConfig.js:53`
 * nombra, y aquí se ve en qué se traduce — no en un error, en pantallas vacías
 * y en acciones de admin que dejan de aparecer. El runbook lo trata como el
 * primer motivo de reversión.
 *
 * ─── LA FORMA DEL CONTRATO VIVE AQUÍ Y EN NINGÚN OTRO SITIO ─────────────────
 *
 * Las pantallas siguen hablando en los nombres que ya usaban (`type: 'info'`,
 * `count`, `name`, `term`). Traducir en el adaptador es lo que permite revertir
 * el interruptor sin tocar una línea de JSX.
 */
import { apiFetch, ApiError } from './client';

// ─── Los topes del servidor, copiados porque pasarse es un 400 ───────────────
// (`api/src/services/audit.ts`: MAX_PAGE_SIZE, MAX_PAGE, MAX_WINDOW_DAYS).
// Recortar aquí evita que la pantalla ofrezca un botón que lleva a un error.

/** 50 por página, y es también el techo del API. */
export const AUDIT_PAGE_SIZE = 50;
/** `OFFSET` obliga a recorrer y descartar: la página 201 es un 400. */
export const AUDIT_MAX_PAGE = 200;
/** Un trimestre. Pedir 93 días es un 400 con su mensaje. */
export const MAX_WINDOW_DAYS = 92;
/** Lo que enseña el panel si no se pide otra cosa. */
export const DEFAULT_WINDOW_DAYS = 30;

/**
 * República Dominicana: UTC-4 FIJO, sin horario de verano desde 2000.
 *
 * 🔴 ESTE NÚMERO TIENE QUE SER EL MISMO QUE `RD_UTC_OFFSET_HOURS` DEL SERVICIO.
 * El servidor corta el día en `-04` porque así están cortadas las particiones;
 * si el navegador calculara "hoy" con la zona del aparato, un admin en Madrid
 * pediría una ventana desplazada y vería la serie corrida un día sin que nada
 * fallara. Por eso no se usa `toLocaleDateString` ni la zona del sistema.
 */
export const RD_UTC_OFFSET_HOURS = 4;

/** Un día dominicano `YYYY-MM-DD` a partir de un instante. */
export function rdDay(at = new Date()) {
  const t = new Date(at.getTime() - RD_UTC_OFFSET_HOURS * 3600_000);
  return t.toISOString().slice(0, 10);
}

/** Suma días a un día dominicano (calendario, no husos). */
export function rdAddDays(day, n) {
  const [y, m, d] = String(day).split('-').map(Number);
  const t = Date.UTC(y, m - 1, d) + n * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Los días de la ventana, TODOS, incluidos los que no tuvieron ni un evento.
 *
 * El API agrupa (`GROUP BY`), así que un día sin actividad NO vuelve como fila.
 * La gráfica de hoy pinta los 30 días con ceros; sin rellenar, el eje saltaría
 * del 3 al 9 de agosto y parecería que la app no existió esa semana.
 */
export function rdDayRange(fromDay, toDay) {
  const dias = [];
  let cur = fromDay;
  // Tope defensivo: la ventana ya viene acotada por el API, esto solo impide un
  // bucle infinito si alguien invierte los extremos.
  for (let i = 0; i <= MAX_WINDOW_DAYS && cur <= toDay; i++) {
    dias.push(cur);
    cur = rdAddDays(cur, 1);
  }
  return dias;
}

/** Página válida para el API: entera, ≥1 y sin pasarse del tope. */
function pagina(v) {
  const n = Math.floor(Number(v) || 1);
  return Math.min(AUDIT_MAX_PAGE, Math.max(1, n));
}

/** Texto con contenido, o nada. Un filtro en blanco no es un filtro. */
function txt(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? undefined : s;
}

const num = (v) => (v === null || v === undefined ? 0 : Number(v) || 0);

/** Quita lo que no viaja. `buildUrl` ya lo hace, pero así el objeto es el que se ve. */
function limpiar(q) {
  for (const k of Object.keys(q)) if (q[k] === undefined) delete q[k];
  return q;
}

/**
 * La envoltura de página que devuelven TODOS los endpoints del dominio.
 *
 * `total` es el del conjunto FILTRADO y viene del mismo viaje
 * (`count(*) OVER ()`): es lo que permite decir "1 de 7" sin que las filas sean
 * de otro 7.
 */
function adaptPage(payload, items, { page, pageSize }) {
  const totalCrudo = payload && payload.total;
  const total = Number.isFinite(Number(totalCrudo)) && totalCrudo !== null && totalCrudo !== undefined
    ? Number(totalCrudo)
    : null;
  const tam = Number(payload && payload.pageSize) || pageSize;
  const paginasDelServidor = Number(payload && payload.totalPages);
  return {
    items,
    total,
    // Sin resultados sigue habiendo UNA página: no existe "página 1 de 0".
    totalPages: Number.isFinite(paginasDelServidor) && paginasDelServidor > 0
      ? paginasDelServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload && payload.page) || page,
    pageSize: tam,
  };
}

/** La lista, o un error. Una lista vacía es una afirmación; un fallo no lo es. */
function lista(payload, que) {
  const l = payload && payload.items;
  if (!Array.isArray(l)) {
    throw new ApiError(`El API no devolvió una lista de ${que}.`, { code: 'bad_response' });
  }
  return l;
}

// ===========================================================================
// 1. EL REGISTRO DE AUDITORÍA
// ===========================================================================

/**
 * Una fila del registro, con los nombres que `AdminAudit.js` ya pinta.
 *
 * `adminName` viene CONGELADO en la fila y no resuelto por JOIN: un registro de
 * auditoría cuyo texto cambia cuando alguien se cambia el nombre no sirve de
 * registro.
 */
export function adaptAuditEntry(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id,
    adminId: row.adminId ?? null,
    adminName: row.adminName ?? '',
    action: row.action ?? '',
    targetType: row.targetType ?? null,
    targetId: row.targetId ?? null,
    targetName: row.targetName ?? null,
    details: row.details ?? null,
    // Texto ISO. `formatDate`/`timeAgo` ya saben leer las dos formas.
    createdAt: row.createdAt ?? null,
  };
}

/**
 * Los filtros del registro, traducidos.
 *
 * ⚠️ `AuditSchema` es `.strict()`: un parámetro que el API no conozca devuelve
 * 400 nombrándolo, no se ignora. Los nombres son los de
 * `api/src/services/audit.ts` y no se inventan.
 *
 * La ventana aquí es OPCIONAL y sin tope, al revés que en la analítica: la tabla
 * no está particionada y "las últimas acciones, sin fecha" es el caso normal del
 * panel.
 */
export function auditLogQuery({
  page = 1, pageSize = AUDIT_PAGE_SIZE,
  adminId, action, targetType, targetId, from, to,
} = {}) {
  return limpiar({
    page: pagina(page),
    pageSize,
    adminId: txt(adminId),
    action: txt(action),
    targetType: txt(targetType),
    targetId: txt(targetId),
    from: txt(from),
    to: txt(to),
  });
}

/** Una página del registro de auditoría. Los errores SUBEN. */
export async function fetchAuditLog(filtros = {}, { apiFetchImpl, signal } = {}) {
  const query = auditLogQuery(filtros);
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/admin/audit', { query, signal });
  const items = lista(payload, 'acciones').map(adaptAuditEntry).filter(Boolean);
  return adaptPage(payload, items, { page: query.page, pageSize: query.pageSize });
}

// ===========================================================================
// 2. ACTIVIDAD — el número que hoy vale 1
// ===========================================================================

/**
 * 🔴 ESTE CAMBIO MUEVE UN NÚMERO QUE EL DUEÑO MIRA, Y HACIA ARRIBA.
 *
 * `AdminAnalytics.js:88` hace `uniqueMonth.add(ev.userId)` sobre `profileEvents`,
 * y el campo de esa colección se llama `viewerId` (`analytics.js:66`). Un `Set`
 * de `undefined` mide 1: por eso DAU y MAU llevan desde el 2026-07-09 valiendo
 * 1 o 0. El API cuenta `count(DISTINCT viewer_id)`, o sea bien.
 *
 * Contar bien no convierte el dato en lo que la etiqueta promete, y eso importa
 * antes de encender el interruptor: `profile_event` solo se escribe cuando
 * alguien ABRE EL PERFIL de un taller o un mecánico, deduplicado por sesión, y
 * la regla de Firestore (`allow create: if isAuth()`) deja fuera al visitante
 * anónimo. No es "usuarios activos": es "usuarios que miraron un negocio".
 * El API lo dice en `metric`/`basis` y aquí se conserva tal cual.
 *
 * El camino de Firestore NO se toca: sigue contando como hasta hoy, con su bug.
 */
export function adaptActivity(payload, { fillDays = true } = {}) {
  const filas = lista(payload, 'días');
  const porDia = new Map();
  for (const r of filas) {
    if (!r || !r.day) continue;
    porDia.set(String(r.day), { viewers: num(r.distinctViewers), events: num(r.events) });
  }

  const from = payload.from || (filas[0] && filas[0].day) || rdDay();
  const to = payload.to || from;
  const dias = fillDays ? rdDayRange(from, to) : [...porDia.keys()];

  return {
    metric: payload.metric ?? null,
    basis: payload.basis ?? null,
    from,
    to,
    /** Personas distintas en TODA la ventana. NO es la suma de los días. */
    distinctViewers: num(payload.distinctViewers),
    events: num(payload.events),
    days: dias.map((day) => ({
      day,
      distinctViewers: porDia.get(day)?.viewers ?? 0,
      events: porDia.get(day)?.events ?? 0,
    })),
  };
}

/**
 * La ventana de la analítica.
 *
 * Sin `from`/`to` el servidor usa los últimos 30 días terminando HOY (día
 * dominicano), que es exactamente lo que el panel pide. Se dejan omitidos a
 * propósito en vez de calcularlos aquí: dos relojes que dicen "hoy" acaban
 * discrepando, y el del servidor es el que corta las particiones.
 */
export function windowQuery({ page = 1, pageSize = AUDIT_PAGE_SIZE, from, to, includeBackfill } = {}) {
  return limpiar({
    page: pagina(page),
    pageSize,
    from: txt(from),
    to: txt(to),
    includeBackfill: includeBackfill === true ? true : undefined,
  });
}

export async function fetchActivity(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/admin/analytics/activity', { query: windowQuery(filtros), signal });
  return adaptActivity(payload);
}

// ===========================================================================
// 3. PANTALLAS Y BÚSQUEDAS
// ===========================================================================

/**
 * Pantallas más vistas, con los nombres que la gráfica ya usa (`name`/`count`).
 *
 * `backfillExcluded` son las vistas que el API dejó fuera por venir del volcado
 * histórico: el acumulado de `analytics/screens` NO tiene fechas, así que se
 * importa como UN día marcado y mezclarlo pintaría un pico que nunca ocurrió.
 * Se descarta y se dice cuánto; quien lo ignore está tirando un dato a la basura
 * en silencio, que es justo lo que el API se negó a hacer.
 */
export function adaptScreens(payload) {
  const items = lista(payload, 'pantallas').map((r) => ({
    name: String(r.screenKey ?? ''),
    count: num(r.views),
    days: num(r.days),
  }));
  return {
    ...adaptPage(payload, items, { page: Number(payload.page) || 1, pageSize: AUDIT_PAGE_SIZE }),
    screens: items,
    from: payload.from ?? null,
    to: payload.to ?? null,
    backfillExcluded: num(payload.backfillExcluded),
  };
}

export async function fetchScreens(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/admin/analytics/screens', { query: windowQuery(filtros), signal });
  return adaptScreens(payload);
}

/**
 * Búsquedas más frecuentes.
 *
 * `users` (personas distintas) es dato nuevo y vale: un término con 200
 * búsquedas de UNA sola persona no es una tendencia, y el contador agregado de
 * Firestore no puede distinguirlo. La pantalla hoy solo pinta `term` y `count`;
 * el resto viaja para quien lo quiera enseñar.
 */
export function adaptSearches(payload) {
  const items = lista(payload, 'búsquedas').map((r) => ({
    term: String(r.term ?? ''),
    count: num(r.total),
    users: num(r.users),
    sample: r.sample ?? '',
  }));
  return {
    ...adaptPage(payload, items, { page: Number(payload.page) || 1, pageSize: AUDIT_PAGE_SIZE }),
    searches: items,
    from: payload.from ?? null,
    to: payload.to ?? null,
  };
}

export async function fetchSearches(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/admin/analytics/searches', { query: windowQuery(filtros), signal });
  return adaptSearches(payload);
}

// ===========================================================================
// 4. ACEPTACIÓN DE TÉRMINOS
// ===========================================================================

/**
 * Hoy NINGUNA pantalla lee esta colección: si un usuario disputa haber aceptado
 * los términos, la respuesta es entrar a la consola de Firebase y buscar a mano.
 * El endpoint existe; el consumidor todavía no. Se publica aquí para que el día
 * que se construya no nazca con su propio `fetch`.
 */
export async function fetchTermsAcceptances(
  { page = 1, pageSize = AUDIT_PAGE_SIZE, userId, version } = {},
  { apiFetchImpl, signal } = {},
) {
  const query = limpiar({ page: pagina(page), pageSize, userId: txt(userId), version: txt(version) });
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/admin/terms-acceptances', { query, signal });
  const items = lista(payload, 'aceptaciones').map((r) => ({
    id: r.id,
    userId: r.userId ?? null,
    version: r.version ?? null,
    method: r.method ?? null,
    ageConfirmed: r.ageConfirmed === true,
    acceptedAt: r.acceptedAt ?? null,
  }));
  return adaptPage(payload, items, { page: query.page, pageSize: query.pageSize });
}

// ===========================================================================
// 5. AVISOS
// ===========================================================================

/**
 * ⚠️ EL TIPO VIENE EN MAYÚSCULAS Y LA PANTALLA LO BUSCA EN MINÚSCULAS.
 *
 * El enum de la base es `INFO|WARNING|MAINTENANCE` (`db/schema.prisma:385`) y
 * `AdminAnnouncements.TYPE_META` tiene las claves `info|warning|maintenance`.
 * Sin bajarlo, cada aviso cae al `|| typeMeta.info` y TODOS salen azules: un
 * aviso de mantenimiento se pinta como informativo y no hay ningún error que lo
 * delate. Es el mismo tropiezo que `vehicleCategory` en el catálogo.
 */
export function adaptAnnouncement(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id,
    title: row.title ?? '',
    body: row.body ?? '',
    type: String(row.type ?? 'INFO').toLowerCase(),
    active: row.active === true,
    startsAt: row.startsAt ?? null,
    endsAt: row.endsAt ?? null,
    createdBy: row.createdBy ?? null,
    // Texto ISO: `formatDate` lo entiende igual que un Timestamp.
    createdAt: row.createdAt ?? null,
    deletedAt: row.deletedAt ?? null,
  };
}

/**
 * Los avisos COMO LOS VE EL PANEL: también los apagados.
 *
 * 🔴 El API ya los devuelve ordenados (`created_at DESC, id ASC`) y sus fechas
 * son texto ISO, así que el `a.createdAt?.toMillis` que usa la pantalla daría 0
 * para todos. Por eso el camino de Postgres NO reordena: el orden es el del
 * servidor, que además desempata por `id` y no puede repetir ni saltarse filas
 * entre páginas.
 */
export async function fetchAnnouncements(
  { page = 1, pageSize = AUDIT_PAGE_SIZE, includeDeleted, active } = {},
  { apiFetchImpl, signal } = {},
) {
  const query = limpiar({
    page: pagina(page),
    pageSize,
    includeDeleted: includeDeleted === true ? true : undefined,
    active: typeof active === 'boolean' ? active : undefined,
  });
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/admin/announcements', { query, signal });
  const items = lista(payload, 'avisos').map(adaptAnnouncement).filter(Boolean);
  return adaptPage(payload, items, { page: query.page, pageSize: query.pageSize });
}

/**
 * EL BANNER que ve el cliente. Única ruta del dominio que no es de admin, y por
 * la regla de Firestore (`allow read: if isAuth()`), no por comodidad.
 *
 * El servidor ya filtra activo + no borrado + dentro de su ventana; aquí no se
 * vuelve a filtrar para que no haya dos criterios de "qué se enseña".
 */
export async function fetchActiveAnnouncements(
  { page = 1, pageSize = AUDIT_PAGE_SIZE } = {},
  { apiFetchImpl, signal } = {},
) {
  const query = { page: pagina(page), pageSize };
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/announcements', { query, signal });
  const items = lista(payload, 'avisos').map(adaptAnnouncement).filter(Boolean);
  return adaptPage(payload, items, { page: query.page, pageSize: query.pageSize });
}

/**
 * El motivo REAL del rechazo, cuando hay que enseñárselo a alguien.
 *
 * Solo se muestra lo que viene del API (`ApiError`, incluidos los fallos de red,
 * que también dicen algo útil). Un `TypeError` nuestro NO: disfrazarlo de
 * respuesta del servidor manda al admin a revisar un filtro que estaba bien.
 */
export function auditErrorReason(err) {
  if (!err || typeof err.message !== 'string') return null;
  if (err.name !== 'ApiError') return null;
  const msg = err.message.trim();
  return msg || null;
}

// ===========================================================================
// 6. LAS ESCRITURAS DEL DOMINIO — y la que NO está aquí
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 NO HAY `logAdminAction` POR API, Y NO ES UN OLVIDO.                    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// El encabezado de este archivo decía que el dominio entero era de solo lectura.
// Ya no lo es: los avisos, la aceptación de términos y los contadores de
// analítica tienen su puerta, porque sin ellas encender el interruptor deja al
// admin publicando avisos que no ve nadie y al panel de analítica leyendo una
// base donde no escribe nadie.
//
// Lo que SIGUE sin puerta, a propósito, es `adminAuditLog`. Un endpoint para
// escribir auditoría es un endpoint para escribir auditoría FALSA: cualquiera
// con un token de admin fabricaría la fila que le conviene. Las filas de
// auditoría las escribe el API dentro de la MISMA transacción que la acción que
// registran. Por eso `src/utils/adminAudit.js` sigue escribiendo en Firestore
// CON EL INTERRUPTOR DONDE ESTÉ: es la única forma de que una acción de admin
// que todavía corre por Firestore deje rastro. Si dejara de registrar, no habría
// ningún error — habría acciones de admin sin huella.
// ===========================================================================

/** Lo que la base admite como tipo de aviso. La app los escribe en minúscula. */
const ANNOUNCEMENT_TYPE_API = { info: 'INFO', warning: 'WARNING', maintenance: 'MAINTENANCE' };

/**
 * PUBLICAR UN AVISO.
 *
 * Cuerpo por lista de PERMITIDOS y jamás con un `...datos`: el esquema del API
 * es `.strict()` y un campo de más devuelve 400 nombrándolo. `createdBy` y
 * `createdAt` NO viajan: los pone el servidor, porque un aviso habla a toda la
 * base con la voz de MekaFix y firmarlo con el nombre de otro es barato.
 */
export async function createAnnouncement({ title, body, type, active } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const titulo = txt(title);
  const cuerpo = txt(body);
  if (!titulo) throw new ApiError('title: el aviso necesita un título.', { code: 'bad_request' });
  if (!cuerpo) throw new ApiError('body: el aviso necesita un texto.', { code: 'bad_request' });
  const payload = await call('/admin/announcements', {
    method: 'POST',
    body: {
      title: titulo,
      body: cuerpo,
      type: ANNOUNCEMENT_TYPE_API[String(type || 'info').toLowerCase()] || 'INFO',
      active: active !== false,
    },
    signal,
  });
  return adaptAnnouncement(payload);
}

/** APAGAR O ENCENDER un aviso. Es el interruptor del panel, nada más. */
export async function setAnnouncementActive(id, active, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return adaptAnnouncement(
    await call(`/admin/announcements/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { active: active === true },
      signal,
    }),
  );
}

/**
 * RETIRAR un aviso.
 *
 * 🔴 NO ES UN BORRADO. En Firestore la pantalla hace `deleteDoc` y un aviso que
 * se envió a toda la base desaparece sin rastro: no se puede saber qué decía ni
 * quién lo publicó. El API lo marca como retirado y lo apaga, que es lo que el
 * admin esperaba del botón, conservando la prueba.
 */
export async function deleteAnnouncement(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return adaptAnnouncement(
    await call(`/admin/announcements/${encodeURIComponent(id)}`, { method: 'DELETE', signal }),
  );
}

/**
 * REGISTRAR LA ACEPTACIÓN DE LOS TÉRMINOS.
 *
 * 🔴 `userId` NO viaja: sale del token. Aceptar los términos en nombre de otro
 * es fabricar la prueba que después te defiende, o sea lo contrario de una
 * prueba. Tampoco se deduplica: aceptar dos veces la misma versión son dos
 * hechos con dos fechas, y perder uno cuesta mucho más que guardar uno de más.
 */
export async function recordTermsAcceptance(
  { version, method = 'signup', ageConfirmed = false } = {},
  { apiFetchImpl, signal } = {},
) {
  const call = apiFetchImpl || apiFetch;
  const v = txt(version);
  if (!v) throw new ApiError('version: falta qué versión se aceptó.', { code: 'bad_request' });
  return call('/terms-acceptances', {
    method: 'POST',
    body: { version: v, method: txt(method) || 'signup', ageConfirmed: ageConfirmed === true },
    signal,
  });
}

/**
 * SUMAR UNA VISTA DE PANTALLA.
 *
 * La suma la hace la base (`views = views + 1`), que es atómico: dos personas en
 * la misma pantalla el mismo día suman dos, no una. La deduplicación por sesión
 * la sigue haciendo el navegador — no es un candado, es un acotador de gasto.
 */
export async function recordScreenView(screenKey, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const k = txt(screenKey);
  if (!k) throw new ApiError('screenKey: falta la pantalla.', { code: 'bad_request' });
  return call('/analytics/screen-views', { method: 'POST', body: { screenKey: k }, signal });
}

/**
 * Las DOS superficies que existen en la base. Mandar otra no es un aviso: es un
 * 500 en el `::` del INSERT, en producción y solo cuando alguien busque desde
 * esa pantalla.
 */
export const SEARCH_SURFACES = ['MARKETPLACE', 'MECHANICS'];

/**
 * GUARDAR UN TÉRMINO BUSCADO. Una fila por búsqueda, no un contador: es lo único
 * que permite saber cuántas PERSONAS distintas buscaron algo, y 200 búsquedas de
 * una sola persona no son una tendencia.
 */
export async function recordSearch({ term, surface, sessionId } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const t = txt(term);
  const s = String(surface || '').toUpperCase();
  const sid = txt(sessionId);
  if (!t) throw new ApiError('term: no hay nada que registrar.', { code: 'bad_request' });
  if (!SEARCH_SURFACES.includes(s)) {
    throw new ApiError(`surface: "${surface}" no es una pantalla de búsqueda.`, { code: 'bad_request' });
  }
  if (!sid) throw new ApiError('sessionId: falta la sesión.', { code: 'bad_request' });
  return call('/analytics/searches', { method: 'POST', body: { term: t, surface: s, sessionId: sid }, signal });
}
